import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer } from "antd";

// assets
import { images } from "../../../assets/images/index";
import List from "../../../assets/customSVG/List";
import Landscape from "../../../assets/customSVG/Landscape";
import { ICONS } from "../../../assets/icons/index";

// helper
import { cn } from "../../../helper/cn";
import {
  addLineBreake,
  replaceFormating,
  replaceVariables,
  replaceVariablesForPreview,
} from "../../../modules/inbox/helper";

// utils
import {
  allowedCtaButtonTypes,
  allowedCustomButtonTypes,
  getCTAButtons,
} from "./Buttons/utils";

// hooks
import useToggle from "../../../hooks/useToggle";
import CTAButtonPreview from "./Buttons/CTAButtonPreview";
import QuickReplayButtonPreview from "./Buttons/QuickReplayButtonPreview";
import CarouselCardPreview from "./CarouselCardPreview";
import Tag from "../../../assets/customSVG/Tag";
import DocSvg from "../../../assets/customSVG/DocSvg";
import DocumentIcon from "../../../assets/customSVG/DocumentIcon";
import ReviewIcon from "../../../assets/customSVG/ReviewIcon";

// Full-screen Order Details View Component
const OrderDetailsView = ({ products, orderDetails, onClose, header, businessName }) => {
  // Calculate subtotal - if tax inclusive, use base prices; otherwise use original prices
  const subtotal = products?.reduce((sum, product) => {
    const price = parseFloat(product.price) || 0;
    const quantity = parseInt(product.quantity) || 1;
    const taxValue = parseFloat(orderDetails?.tax) || 0;

    // If tax is inclusive, calculate base price
    let itemPrice = price;
    if (orderDetails?.tax_type === 'tax_inclusive' && taxValue > 0) {
      itemPrice = price / (1 + taxValue / 100);
    }

    return sum + (itemPrice * quantity);
  }, 0) || 0;

  const discountValue = parseFloat(orderDetails?.discount) || 0;
  const discountAmount = orderDetails?.discount_type === 'percent'
    ? (subtotal * discountValue) / 100
    : discountValue;
  const discountDescription = orderDetails?.discount_description
    ? orderDetails.discount_description.length > 15
      ? `${orderDetails.discount_description.substring(0, 15)}...`
      : orderDetails.discount_description
    : '';

  const shipping = parseFloat(orderDetails?.shipping) || 0;
  const shippingDescription = orderDetails?.shipping_description
    ? orderDetails.shipping_description.length > 15
      ? `${orderDetails.shipping_description.substring(0, 15)}...`
      : orderDetails.shipping_description
    : '';

  // Handle tax calculation based on type
  const taxValue = parseFloat(orderDetails?.tax) || 0;
  let taxAmount = 0;
  const amountAfterDiscount = subtotal - discountAmount;

  if (orderDetails?.tax_type === 'tax_inclusive') {
    // Tax is already included in the prices
    // Formula: taxAmount = amountAfterDiscount * (taxValue / 100)
    taxAmount = amountAfterDiscount * (taxValue / 100);
  } else if (orderDetails?.tax_type === 'tax_exclusive') {
    // Tax needs to be added on top
    taxAmount = (amountAfterDiscount * taxValue) / 100;
  } else {
    // If tax_type is not specified or is a fixed amount
    taxAmount = taxValue;
  }

  const taxDescription = orderDetails?.tax_description
    ? orderDetails.tax_description.length > 15
      ? `${orderDetails.tax_description.substring(0, 15)}...`
      : orderDetails.tax_description
    : '';

  // Calculate total
  let total;
  if (orderDetails?.tax_type === 'tax_inclusive') {
    // For inclusive tax: total = base subtotal + tax + shipping - discount
    // The tax was extracted from original prices, so we add it back to get the original total
    total = subtotal - discountAmount + taxAmount + shipping;
  } else {
    // For exclusive tax: total = subtotal - discount + shipping + tax
    total = subtotal - discountAmount + shipping + taxAmount;
  }

  // Default business info
  const orderNumber = "{{order_number}}";

  return (
    <div className="w-full h-full bg-[#F0F2F5] flex flex-col relative">
      {/* Header */}
      <div className="bg-[#00897B] text-white px-[0.5vw] py-[0.4vw] flex items-center gap-[0.3vw]">
        <button onClick={onClose} className="hover:opacity-80">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h2 className="text-[0.9vw] font-medium">Order details</h2>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Business Info Section */}
        <div className="bg-white mt-[0.5vw] mx-[0.5vw] rounded-[0.4vw] px-[0.5vw] py-[0.6vw] text-center">
          {/* Business Logo */}
          <div className="size-[3vw] mx-auto mb-[0.3vw] rounded-full font-xs border border-gray-300 flex items-center justify-center bg-[var(--white)]">
            Logo
          </div>

          {/* Business Name */}
          <h3 className="text-[0.85vw] font-medium text-gray-900 mb-[0.1vw]">{businessName}</h3>

          {/* Order Number */}
          <p className="text-[0.7vw] text-gray-500 mb-[0.4vw]">ORDER #{orderNumber}</p>

          {/* Order Status Badge */}
          <div className="inline-flex items-center gap-[0.2vw] bg-gray-100 px-[0.5vw] py-[0.2vw] rounded-full">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="6.5" stroke="#666" strokeWidth="1.2"/>
              <path d="M8 4.5V8L10 10" stroke="#666" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <span className="text-[0.7vw] text-gray-700">Order pending</span>
          </div>
        </div>

        {/* Products List */}
        <div className="bg-white mx-[0.5vw] mt-[0.5vw] rounded-[0.4vw] overflow-hidden">
          {products?.map((product, index) => {
            const price = parseFloat(product.price) || 0;
            const quantity = parseInt(product.quantity) || 1;
            // Always show the original price (as entered by user)
            const itemTotal = price * quantity;

            return (
              <div key={index} className="flex items-center justify-between px-[0.5vw] py-[0.5vw] border-b border-gray-100 last:border-b-0">
                <div className="flex items-start gap-[0.4vw] flex-1">
                  {/* Cart Icon */}
                  <div className="w-[2vw] h-[2vw] bg-gray-100 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {product?.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name || 'Product'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img 
                        src={ICONS?.eCommerce}
                        alt="" 
                        style={{ width: "19px", filter: 'invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)' }}
                      />
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[0.8vw] text-gray-900 font-normal mb-[0.1vw] truncate">{product.name || 'Product Name'}</p>
                    <p className="text-[0.7vw] text-gray-500">Quantity {String(quantity).padStart(2, '0')}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="text-[0.8vw] text-gray-900 font-normal ml-[0.3vw] flex-shrink-0">₹{itemTotal.toFixed(2)}</div>
              </div>
            );
          })}
        </div>

        {/* Order Summary */}
        <div className="mx-[0.5vw] mt-[0.5vw] mb-[5vw] space-y-[0.2vw] px-[0.2vw]">
          {/* Subtotal */}
          <div className="flex justify-between text-[0.7vw]">
            <span className="text-gray-400">Subtotal</span>
            <span className="text-gray-400">₹{subtotal.toFixed(2)}</span>
          </div>

          {/* Discount */}
          {discountAmount > 0 && (
            <div className="flex justify-between text-[0.7vw]">
              <span className="text-gray-400">
                Discount {discountDescription ? `(${discountDescription})` : orderDetails.discount_type === 'percent' ? `(${discountValue}%)` : ''}
              </span>
              <span className="text-gray-400">-₹{discountAmount.toFixed(2)}</span>
            </div>
          )}

          {/* Shipping */}
          {shipping > 0 && (
            <div className="flex justify-between text-[0.7vw]">
              <span className="text-gray-400">Shipping {shippingDescription ? `(${shippingDescription})` : ''}</span>
              <span className="text-gray-400">₹{shipping.toFixed(2)}</span>
            </div>
          )}

          {/* Tax */}
          {taxAmount > 0 && (
            <div className="flex justify-between text-[0.7vw]">
              <span className="text-gray-400">
                Tax {taxDescription ? `(${taxDescription})` : ''}
              </span>
              <span className="text-gray-400">₹{taxAmount.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer with Total and Continue Button - Fixed at bottom */}
      <div className="bg-white border-t border-gray-200 px-[0.5vw] py-[0.5vw]">
        {/* Total */}
        <div className="flex justify-between items-center mb-[0.4vw]">
          <span className="text-[0.9vw] font-medium text-gray-900">Total</span>
          <span className="text-[0.9vw] font-bold text-gray-900">₹{total.toFixed(2)}</span>
        </div>

        {/* Continue Button */}
        <button className="w-full bg-[#00897B] text-white py-[0.4vw] rounded-[0.4vw] text-[0.8vw] font-medium hover:bg-[#00796B] transition-colors">
          Continue
        </button>
      </div>
    </div>
  );
};

const TemplatePreview = ({
  category,
  header,
  body,
  bodyVariables,
  footer,
  buttons,
  buttonsOrder,
  ctaButtons = [],
  customButtons = [],
  selectedTemplate,
  useTemplates,
  drawerType = "normal",
  authentication,
  carousel,
  className,
  bodyClassName,
  carouseClassName,
  products = [],
  orderDetails = { discount: 0, shipping: 0, tax: 0 },
  businessName,
  onProductChange = () => {},
  onOrderDetailChange = () => {},
  onRemoveProduct = () => {},
  onAddProduct = () => {},
}) => {
  const [openDrawer, setOpenDrawer] = useState(false);

  // Close drawer when template changes
  useEffect(() => {
    setOpenDrawer(false);
  }, [selectedTemplate]);

  const toggleDrawer = useCallback(() => {
    setOpenDrawer(prev => !prev);
  }, []);

  // Memoize button calculations
  const { orderedButtons, visibleButtons, hasMoreButtons } = useMemo(() => {
    const total = buttonsOrder === "ctaButtons"
      ? [...ctaButtons, ...customButtons]
      : buttonsOrder === "custom"
        ? [...customButtons, ...ctaButtons]
        : [];

    const visible = total.length <= 3 ? total : total.slice(0, 2);
    const hasMore = total.length > 3;

    return {
      orderedButtons: total,
      visibleButtons: visible,
      hasMoreButtons: hasMore
    };
  }, [ctaButtons, customButtons, buttonsOrder]);

  // Memoize footer visibility
  const shouldShowFooter = useMemo(() => {
    return getCTAButtons(buttons, "OPT_OUT")?.length < 1;
  }, [buttons]);

  // Memoize processed body content
  const processedBodyContent = useMemo(() => {
    const content = useTemplates
      ? replaceVariablesForPreview(
        addLineBreake(replaceFormating(body)),
        bodyVariables
      )
      : addLineBreake(replaceFormating(body));
    return content;
  }, [body, bodyVariables, useTemplates]);

  // Memoize header content
  const processedHeaderText = useMemo(() => {
    if (header?.format !== "TEXT") return header?.text;

    return useTemplates && header?.example?.header_text?.[0] !== 0
      ? replaceVariables(header?.text, header?.example?.header_text)
      : header?.text;
  }, [header, useTemplates]);

  const btnStyle = "w-full flex items-center justify-center gap-[0.5vw] py-[0.5vw] font-sm weight-medium border-t-[0.08vw] border-[var(--border-50)] cursor-default";

  const renderButton = useCallback((button, index) => {
    const key = `preview-button-${index}`;

    if (allowedCtaButtonTypes.includes(button?.type)) {
      if(button.type === "FLOW") {
        return (
          <div key={key} className={btnStyle}>
            {
              button?.icon == 'DEFAULT' ? (
                <DocSvg height="14" width="14" color="#007aff" />
              ) : button?.icon == 'DOCUMENT' ? (
                <DocumentIcon height="14" width="14" color="#007aff" />
              ) : button?.icon == 'PROMOTION' ? (
                <Tag height="14" width="14" fill="#007aff" />
              ) : button?.icon == 'REVIEW' ? (
                <ReviewIcon height="14" width="14" color="#007aff" />
              ) : null
            }
            <p>{button.text}</p>
          </div>
        );
      }

      const buttonConfig = {
        URL: { icon: ICONS?.nounLink, text: button.text },
        PHONE_NUMBER: { icon: ICONS?.call, text: button.text },
        COPY_CODE: { icon: ICONS?.CopyLeft, text: "Copy Offer Code" }
      };

      const config = buttonConfig[button.type];
      if (!config) return null;

      return (
        <div key={key}>
          <button className={btnStyle}>
            <img
              src={config.icon}
              alt="button-icon"
              className="size-[0.9vw]"
            />
            {config.text}
          </button>
        </div>
      );
    }

    if (allowedCustomButtonTypes?.includes(button?.type)) {
      return (
        <div key={key} className={btnStyle}>
          <img
            src={ICONS?.shareLink}
            alt="share-icon"
            className="size-[0.9vw]"
          />
          <p>{button.text}</p>
        </div>
      );
    }

    return null;
  }, [btnStyle]);

  const renderHeaderContent = useCallback(() => {
    if (!header) return null;

    switch (header.format) {
      case "TEXT":
        return (
          <div className="h-full font-md weight-semibold break-words">
            {processedHeaderText}
          </div>
        );

      case "IMAGE":
        return header?.example?.header_handle?.length > 0 ? (
          <img
            src={header.example.header_handle[0]}
            className="w-full h-full object-cover rounded-[0.4vw]"
            alt="header"
          />
        ) : null;

      case "VIDEO":
        return header?.example?.header_handle?.length > 0 ? (
          <video
            className="object-cover rounded-[0.4vw]"
            controls
            autoPlay
          >
            <source
              src={header.example.header_handle[0]}
              type="video/mp4"
            />
          </video>
        ) : null;

      case "DOCUMENT":
        return header?.example?.header_handle?.length > 0 ? (
          <iframe
            title="document-preview"
            className="imagePreview"
            src={header.example.header_handle[0]}
            type="application/pdf"
            width="100%"
            height="150px"
          />
        ) : null;

      default:
        return null;
    }
  }, [header, processedHeaderText]);

  const renderAuthenticationTemplate = useCallback(() => (
    <div className="min-h-[4vw] bg-white rounded-[0.7vw] py-[0.5vw] overflow-y-auto">
      <div className="font-md weight-small px-[0.5vw] break-words bg-white">
        {authentication?.body}
      </div>

      {authentication?.content?.expiryTimeForCode && (
        <p className="w-full font-sm weight-small bg-white px-[0.5vw] text-[var(--font-600)] pt-[0.5vw]">
          {`This code expires in ${authentication?.content?.expiryTime >= 1 && authentication?.content?.expiryTime <= 90
            ? authentication.content.expiryTime
            : 0
            } Minutes.`}
        </p>
      )}

      <div className="h-auto px-[0.5vw] bg-white rounded-b-[0.7vw] mt-[0.8vw] text-[#007AFF]">
        {authentication?.buttons?.map((button, index) => (
          <div key={`authentication-button-${index}`}>
            <button className={btnStyle}>{button?.text}</button>
          </div>
        ))}
      </div>
    </div>
  ), [authentication, btnStyle]);

  // State for order details view
  const [showOrderDetailsView, setShowOrderDetailsView] = useState(false);

  const openOrderDetailsView = () => {
    setShowOrderDetailsView(true);
  };

  const closeOrderDetailsView = () => {
    setShowOrderDetailsView(false);
  };

  const renderOrderDetailTemplate = useCallback(({ products, orderDetails }) => {
    return (
      <div className="min-h-[4vw] bg-white rounded-[0.7vw] px-[0.3vw] py-[0.3vw] overflow-y-auto">
        {/* Order Header */}
        <div className="px-[0.5vw] py-[0.5vw] bg-[#0000000A] rounded-[0.4vw]">
          <div className="flex justify-between items-center">
            <span className="text-[0.7vw] text-[#606770]">ORDER # {'{{Order number}}'}</span>
          </div>
          <div className="border-t border-dashed border-[#0000001A] my-[0.3vw]"></div>
          <div className="flex flex-row items-center gap-3">
            {(() => {
              // Check if there's a product with an image_url
              const productWithImage = products?.find(p => p.image_url);
              
              if (productWithImage?.image_url) {
                return (
                  <img
                    src={productWithImage.image_url}
                    alt="Product"
                    className="w-[45px] h-[45px] object-cover rounded-md"
                  />
                );
              }
              
              // Fallback to header image if no product image
              if (header?.format === "IMAGE" && header?.example?.header_handle?.length > 0) {
                return (
                  <img
                    src={header?.example?.header_handle?.[0]}
                    alt=""
                    className="w-[45px] h-[45px] object-cover rounded-md"
                  />
                );
              }
              
              // Default placeholder
              return (
                <div className="flex items-center justify-center bg-[#A3A7AC] p-[0.5vw] rounded-md">
                  <div className="flex items-center justify-center">
                    <Landscape color="#FFFFFF" width={30} height={30} />
                  </div>
                </div>
              );
            })()}
            <div className="flex flex-col gap-1">
              <span className="text-[0.8vw]">
                {products?.map(p => p.name).filter(Boolean).join(', ') || `{{Product name}}`}
              </span>
              <span className="text-[0.7vw] text-[#606770]">
                Quantity {products?.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0) || '{{#}}'}
              </span>
            </div>
          </div>

          {(() => {
            // Calculate subtotal
            const subtotal = products?.reduce((sum, p) => {
              const price = parseFloat(p.price) || 0;
              const qty = parseInt(p.quantity, 10) || 0;
              const taxValue = parseFloat(orderDetails?.tax) || 0;

              // If tax is inclusive, calculate base price
              let itemPrice = price;
              if (orderDetails?.tax_type === 'tax_inclusive' && taxValue > 0) {
                itemPrice = price / (1 + taxValue / 100);
              }

              return sum + (itemPrice * qty);
            }, 0) || 0;

            // Calculate discount amount based on type
            const discountValue = parseFloat(orderDetails?.discount) || 0;
            const discountAmount = orderDetails?.discount_type === 'percent'
            ? (subtotal * discountValue) / 100
            : discountValue;

            // Calculate shipping
            const shipping = parseFloat(orderDetails?.shipping) || 0;

            // Calculate tax (applied after discount)
            const taxValue = parseFloat(orderDetails?.tax) || 0;
            let taxAmount = 0;

            if (orderDetails?.tax_type === 'tax_inclusive') {
              // Tax is already included in the prices
              // Formula: taxAmount = (totalWithTax * taxRate) / (100 + taxRate)
              const amountAfterDiscount = subtotal - discountAmount;
              taxAmount = amountAfterDiscount * (taxValue / 100);
            } else if (orderDetails?.tax_type === 'tax_exclusive') {
              // Tax needs to be added on top
              taxAmount = ((subtotal - discountAmount) * taxValue) / 100;
            } else {
              // If tax_type is not specified or is a fixed amount
              taxAmount = taxValue;
            }

            // Calculate total
            let total;
            if (orderDetails?.tax_type === 'tax_inclusive') {
              // For inclusive tax: total = base subtotal + tax + shipping - discount
              // The tax was extracted from original prices, so we add it back to get the original total
              total = subtotal - discountAmount + taxAmount + shipping;
            } else {
              // For exclusive tax: total = subtotal - discount + shipping + tax
              total = subtotal - discountAmount + shipping + taxAmount;
            }

            return (
              <>
                <div className="border-t border-dashed border-[#0000001A] my-[0.3vw]"></div>
                <div className="justify-between flex">
                  <span className="text-[0.7vw] text-[#606770]">Total</span>
                  <span className="text-[0.7vw] text-[#606770] font-medium">
                    ₹{total.toFixed(2)}
                  </span>
                </div>
              </>
            );
          })()}
        </div>

        {/* Header */}
        {header?.format === "TEXT" && (
          <div className="w-full px-[0.5vw] bg-white rounded-[0.4vw] h-full overflow-hidden">
            {renderHeaderContent()}
          </div>
        )}

        {/* Body */}
        <p
          className="font-md weight-small px-[0.5vw] pt-[0.5vw] break-words bg-white"
          dangerouslySetInnerHTML={{ __html: processedBodyContent }}
        />

        {/* Footer */}
        {footer?.text && shouldShowFooter && (
          <p className="w-full font-md weight-small bg-white px-[0.5vw] text-[var(--font-600)] pt-[0.5vw]">
            {footer.text}
          </p>
        )}

        {/* Order Detail Button */}
        <div className="h-auto px-[0.5vw] bg-white rounded-b-[0.7vw] mt-[0.8vw] text-xs">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openOrderDetailsView();
            }}
            className="w-full text-[#007AFF] text-center py-2 px-4 rounded-md hover:bg-blue-50 transition-colors"
          >
            Review and pay
          </button>
        </div>
      </div>
    );
  }, [header, processedBodyContent, footer, shouldShowFooter]);

  const renderRegularTemplate = useCallback(() => (
    <div className="min-h-[4vw] bg-white rounded-[0.7vw] py-[0.5vw] overflow-y-auto">
      {/* Header */}
      <div className="w-full px-[0.5vw] bg-white rounded-[0.4vw] h-full overflow-hidden">
        {renderHeaderContent()}
      </div>

      {/* Body */}
      <p
        className="font-md weight-small px-[0.5vw] pt-[0.5vw] break-words bg-white"
        dangerouslySetInnerHTML={{ __html: processedBodyContent }}
      />

      {/* Footer */}
      {footer?.text && shouldShowFooter && (
        <p className="w-full font-md weight-small bg-white px-[0.5vw] text-[var(--font-600)] pt-[0.5vw]">
          {footer.text}
        </p>
      )}

      {!shouldShowFooter && (
        <p className="w-full font-md weight-small bg-white px-[0.5vw] text-[var(--font-600)] pt-[0.5vw]">
          Not interested? Tap Stop promotions
        </p>
      )}

      {/* Buttons */}
      <div className="h-auto px-[0.5vw] bg-white rounded-b-[0.7vw] mt-[0.8vw] text-[#007AFF]">
        {visibleButtons.map(renderButton)}

        {hasMoreButtons && (
          <button
            className="w-full h-[2vw] flex items-center justify-center gap-[0.5vw] font-md weight-medium border-t-[0.08vw] border-[var(--border-50)]"
            onClick={toggleDrawer}
          >
            <List color="#007AFF" width="0.9vw" height="0.9vw" />
            See all options
          </button>
        )}
      </div>
    </div>
  ), [
    renderHeaderContent,
    processedBodyContent,
    footer,
    shouldShowFooter,
    visibleButtons,
    hasMoreButtons,
    renderButton,
    toggleDrawer
  ]);

  const renderCarouselSection = useCallback(() => {
    if (category !== "CAROUSEL" || !carousel?.cards?.length) return null;

    return (
      <div className="w-full flex-1 h-auto">
        <div className={cn(
          "min-h-[4vw] flex gap-2.5 overflow-x-auto pb-2 listScroll px-[1vw]",
          carouseClassName
        )}>
          {carousel.cards.map((card, index) => (
            <div key={`carousel-card-${index}`} className="w-full min-w-[12vw] min-h-[13rem]">
              <CarouselCardPreview data={card} carousel={carousel} />
            </div>
          ))}
        </div>
      </div>
    );
  }, [category, carousel, carouseClassName]);

  const renderDrawerButtons = useCallback(() => {
    if (!buttonsOrder) return null;

    const isCtaFirst = buttonsOrder === "ctaButtons";
    const firstButtons = isCtaFirst ? ctaButtons : customButtons;
    const secondButtons = isCtaFirst ? customButtons : ctaButtons;
    const FirstComponent = isCtaFirst ? CTAButtonPreview : QuickReplayButtonPreview;
    const SecondComponent = isCtaFirst ? QuickReplayButtonPreview : CTAButtonPreview;
    const firstProp = isCtaFirst ? "ctaButtons" : "customButtons";
    const secondProp = isCtaFirst ? "customButtons" : "ctaButtons";

    return (
      <>
        {firstButtons?.length > 0 && (
          <div>
            <FirstComponent type={drawerType} {...{ [firstProp]: firstButtons }} />
          </div>
        )}
        {secondButtons?.length > 0 && (
          <div className={firstButtons?.length > 0 ? "border-t-[0.08vw] border-[var(--border-50)]" : ""}>
            <SecondComponent type={drawerType} {...{ [secondProp]: secondButtons }} />
          </div>
        )}
      </>
    );
  }, [buttonsOrder, ctaButtons, customButtons, drawerType]);

  return (
    <div className="w-full h-full relative">
      {/* Show Order Details View when active */}
      {showOrderDetailsView && category === "ORDER_DETAIL" ? (
        <OrderDetailsView
          products={products}
          orderDetails={orderDetails}
          onClose={closeOrderDetailsView}
          header={header}
          businessName={businessName}
        />
      ) : (
        <div
          className={cn(
            "w-full h-full relative overflow-y-auto rounded-[1vw]",
            className
          )}
          style={{ backgroundImage: `url(${images?.bg})` }}
        >
          <div className="w-full h-full relative">
            <div className="w-full h-full overflow-y-auto py-[1vw] flex flex-col gap-[0.7vw]">
              <div className={cn("w-full h-auto px-[1vw]", bodyClassName)}>
                {category === "COPY_CODE"
                  ? renderAuthenticationTemplate()
                  : category === "ORDER_DETAIL"
                    ? renderOrderDetailTemplate({
                        products,
                        orderDetails,
                        onProductChange,
                        onOrderDetailChange,
                        onRemoveProduct,
                        onAddProduct
                      })
                    : renderRegularTemplate()
                }
              </div>

              {renderCarouselSection()}
            </div>
          </div>
        </div>
      )}
      <Drawer
        placement="bottom"
        closable={false}
        onClose={toggleDrawer}
        open={openDrawer}
        getContainer={false}
        rootClassName="template-preview-drawer"
        className={`w-full bottom-[300px] ${drawerType === "small" ? "!rounded-t-[2vw]" : "!rounded-t-[1vw]"}`}
        styles={{
          body: {
            padding: drawerType === "small" ? "0.5vw 1.5vw 0" : "0.5vw 1.5vw 0"
          },
          mask: {
            background: 'rgba(0, 0, 0, 0.45)'
          }
        }}
      >
        <div className={`w-full h-auto ${drawerType === "small" ? "min-h-[10vh]" : "min-h-[30vh]"}`}>
          <div
            className={`w-[5vw] h-[0.4vw] rounded-[0.7vw] mx-auto bg-[var(--font-300)] cursor-pointer ${
              drawerType === "small" ? "mb-[1vw]" : "mb-[1vw]"
            }`}
            onClick={toggleDrawer}
          />

          <div className={`w-full flex items-center gap-[0.5vw] font-normal weight-medium cursor-pointer ${drawerType === "small" ? "h-[2vh] mb-[0.8vw] p-[0.3vw]" : "h-[3vh] mb-[0.8vw] p-[0.5vw]"
            }`}>
            <div className="w-full text-center">All Options</div>
          </div>

          <div className="w-full max-h-[30vh] overflow-auto">
            {renderDrawerButtons()}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default TemplatePreview;